import React, { useState } from 'react';
import { ArchiveBoxIcon, PlusIcon, MinusIcon, BoltIcon } from '@heroicons/react/24/outline';

interface SplitQuantityModalProps {
  item: any;
  boxes: any[];
  onSplit: (splits: { boxId: string; quantity: number }[], newBoxQuantity?: number) => void;
  onCancel: () => void;
  onAddNewBox: (quantity: number) => void;
  onQuickSplit?: (qtyPerBox: number, boxDimensions: { length: number; width: number; height: number }) => void;
}

// Increment/Decrement Button Component
const QuantityControl: React.FC<{
  value: number;
  onChange: (value: number) => void;
  max: number;
  min?: number;
}> = ({ value, onChange, max, min = 0 }) => {
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-10 h-10 flex items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed active:bg-slate-100"
      >
        <MinusIcon className="w-5 h-5" />
      </button>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Math.max(min, Math.min(max, parseInt(e.target.value) || min)))}
        className="w-14 h-10 px-2 border border-slate-300 rounded-lg text-center text-base font-semibold"
      />
      <button
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-10 h-10 flex items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed active:bg-slate-100"
      >
        <PlusIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

export const SplitQuantityModal: React.FC<SplitQuantityModalProps> = ({
  item,
  boxes,
  onSplit,
  onCancel,
  onAddNewBox,
  onQuickSplit,
}) => {
  const [mode, setMode] = useState<'manual' | 'quick'>('manual');
  const [splits, setSplits] = useState<{ boxId: string; quantity: number }[]>([]);
  const [newBoxQuantity, setNewBoxQuantity] = useState<number>(0);

  // Quick split settings
  const [quickSplitQty, setQuickSplitQty] = useState<number>(1);
  const [quickSplitLength, setQuickSplitLength] = useState<number>(12);
  const [quickSplitWidth, setQuickSplitWidth] = useState<number>(12);
  const [quickSplitHeight, setQuickSplitHeight] = useState<number>(6);

  const totalAssigned = splits.reduce((sum, split) => sum + split.quantity, 0) + newBoxQuantity;
  const remaining = item.quantity - totalAssigned;

  const handleQuantityChange = (boxId: string, quantity: number) => {
    const numQty = Math.max(0, Math.min(quantity, item.quantity));

    setSplits(prev => {
      const existing = prev.find(s => s.boxId === boxId);
      if (existing) {
        if (numQty === 0) {
          return prev.filter(s => s.boxId !== boxId);
        }
        return prev.map(s => s.boxId === boxId ? { ...s, quantity: numQty } : s);
      } else if (numQty > 0) {
        return [...prev, { boxId, quantity: numQty }];
      }
      return prev;
    });
  };

  const handleSplit = () => {
    if (mode === 'quick' && onQuickSplit) {
      if (quickSplitQty < 1 || quickSplitQty > item.quantity) {
        alert('Invalid quantity per box');
        return;
      }
      onQuickSplit(quickSplitQty, {
        length: quickSplitLength,
        width: quickSplitWidth,
        height: quickSplitHeight,
      });
      return;
    }

    if (totalAssigned === 0) {
      alert('Please assign at least 1 unit to a box');
      return;
    }

    if (totalAssigned > item.quantity) {
      alert(`You've assigned ${totalAssigned} units, but only ${item.quantity} are available`);
      return;
    }

    const validSplits = splits.filter(s => s.quantity > 0);
    onSplit(validSplits, newBoxQuantity > 0 ? newBoxQuantity : undefined);
  };

  return (
    <div className="fixed inset-0 bg-white lg:bg-black lg:bg-opacity-50 flex lg:items-center lg:justify-center z-[60]" onClick={onCancel}>
      <div
        className="bg-white w-full h-full lg:h-auto lg:rounded-xl lg:shadow-strong lg:max-w-lg lg:m-4 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-slate-900">Split Quantity</h3>
          <p className="text-sm text-slate-500 truncate">
            {item.title}{item.variant_title ? ` - ${item.variant_title}` : ''}
          </p>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0 px-4 py-4 space-y-4">
          {/* Mode Toggle */}
          <div className="flex bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => setMode('manual')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded transition-colors ${
                mode === 'manual'
                  ? 'bg-white text-brand-blue shadow-sm'
                  : 'text-slate-600'
              }`}
            >
              Manual Split
            </button>
            <button
              onClick={() => setMode('quick')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded transition-colors flex items-center justify-center gap-1 ${
                mode === 'quick'
                  ? 'bg-white text-brand-blue shadow-sm'
                  : 'text-slate-600'
              }`}
            >
              <BoltIcon className="w-4 h-4" />
              Quick Split
            </button>
          </div>

          {/* Total Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-blue-900">Total: {item.quantity} units</p>
                <p className="text-xs text-blue-700">
                  {mode === 'manual' ? 'Assign to boxes below' : 'Auto-create boxes'}
                </p>
              </div>
              {mode === 'manual' && (
                <div className="text-right">
                  <p className={`text-2xl font-bold ${remaining < 0 ? 'text-red-600' : 'text-blue-900'}`}>
                    {remaining}
                  </p>
                  <p className="text-xs text-blue-700">remaining</p>
                </div>
              )}
            </div>
          </div>

          {/* Manual Mode */}
          {mode === 'manual' && (
            <>
              {/* Existing Boxes */}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">Assign to Existing Boxes:</p>
                {boxes.map((box, index) => (
                  <div key={box.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                    <p className="text-sm font-medium text-slate-900">Box {index + 1}</p>
                    <QuantityControl
                      value={splits.find(s => s.boxId === box.id)?.quantity || 0}
                      onChange={(val) => handleQuantityChange(box.id, val)}
                      max={item.quantity}
                    />
                  </div>
                ))}
              </div>

              {/* Add to New Box */}
              <div className="border-t border-slate-200 pt-4">
                <p className="text-sm font-semibold text-slate-700 mb-2">Or Add to New Box:</p>
                <div className="flex items-center justify-between bg-green-50 rounded-lg p-3 border-2 border-dashed border-green-300">
                  <div className="flex items-center gap-2">
                    <ArchiveBoxIcon className="w-5 h-5 text-green-700" />
                    <p className="text-sm font-medium text-green-800">New Box</p>
                  </div>
                  <QuantityControl
                    value={newBoxQuantity}
                    onChange={setNewBoxQuantity}
                    max={item.quantity}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1.5">
                  A new box will be created with this quantity
                </p>
              </div>
            </>
          )}

          {/* Quick Split Mode */}
          {mode === 'quick' && onQuickSplit && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm font-medium text-green-800 mb-3">
                  Auto-create boxes with equal quantities
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-green-700">Quantity per box:</p>
                  <QuantityControl
                    value={quickSplitQty}
                    onChange={setQuickSplitQty}
                    max={item.quantity}
                    min={1}
                  />
                </div>
                <p className="text-sm font-semibold text-green-900 mt-3">
                  → Will create {Math.ceil(item.quantity / quickSplitQty)} boxes
                </p>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <p className="text-sm font-semibold text-slate-700 mb-3">Box Dimensions:</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Length</label>
                    <input
                      type="number"
                      value={quickSplitLength}
                      onChange={(e) => setQuickSplitLength(parseFloat(e.target.value) || 12)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-center"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Width</label>
                    <input
                      type="number"
                      value={quickSplitWidth}
                      onChange={(e) => setQuickSplitWidth(parseFloat(e.target.value) || 12)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-center"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Height</label>
                    <input
                      type="number"
                      value={quickSplitHeight}
                      onChange={(e) => setQuickSplitHeight(parseFloat(e.target.value) || 6)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-center"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex-shrink-0 flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 min-h-[44px]"
          >
            Cancel
          </button>
          <button
            onClick={handleSplit}
            disabled={mode === 'manual' ? (totalAssigned === 0 || totalAssigned > item.quantity) : false}
            className="flex-1 px-4 py-3 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-brand-blue-hover disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
          >
            {mode === 'quick'
              ? `Create ${Math.ceil(item.quantity / quickSplitQty)} Boxes`
              : totalAssigned === 0 ? 'Select quantities'
              : totalAssigned > item.quantity ? 'Too many assigned'
              : 'Apply Split'
            }
          </button>
        </div>
      </div>
    </div>
  );
};
